import apiClient from '../apiClient';
import { extractDataArray, parseApiResponse } from '../utils/apiResponseUtils';
import { handleApiError, withErrorHandling } from '../utils/apiErrorUtils';

export interface CrudEntity {
  id: number;
  created_at?: string;
  updated_at?: string;
}

export interface ListParams {
  [key: string]: any;
}

export interface CreateData {
  [key: string]: any;
}

export interface UpdateData {
  [key: string]: any;
}

export interface CrudServiceConfig {
  basePath: string;
  entityName: string;
  arrayKeys?: string[];
}

/**
 * Base CRUD service class that provides common operations
 * for entity management with consistent error handling and response parsing
 */
export abstract class BaseCrudService<
  T extends CrudEntity,
  TCreate extends CreateData = CreateData,
  TUpdate extends UpdateData = UpdateData,
  TListParams extends ListParams = ListParams
> {
  protected readonly config: CrudServiceConfig;

  constructor(config: CrudServiceConfig) {
    this.config = {
      arrayKeys: ['data', 'results', 'items'],
      ...config,
    };
  }

  /**
   * Get all entities with optional filtering
   */
  async getAll(params?: TListParams): Promise<T[]> {
    try {
      const response = await apiClient.get<any>(this.config.basePath, { params });
      
      // console.log(`${this.config.entityName} API Response:`, response.data);
      
      return extractDataArray<T>(response.data, this.config.arrayKeys);
    } catch (error) {
      console.error(`Failed to fetch ${this.config.entityName}:`, error);
      // Return empty array instead of throwing to prevent UI breaks
      return [];
    }
  }

  /**
   * Get a single entity by ID
   */
  async getById(id: number): Promise<T | null> {
    try {
      console.log(`Fetching ${this.config.entityName} details from: ${this.config.basePath}${id}/`);
      const response = await apiClient.get<T>(`${this.config.basePath}${id}/`);
      console.log(`${this.config.entityName} ${id} details:`, response.data);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Create a new entity
   */
  async create(data: TCreate): Promise<T> {
    try {
      console.log(`Creating ${this.config.entityName} with data:`, data);
      const response = await apiClient.post<T>(this.config.basePath, data);
      console.log(`Created ${this.config.entityName}:`, response.data);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Update an existing entity
   */
  async update(id: number, data: TUpdate): Promise<T> {
    try {
      console.log(`Updating ${this.config.entityName} ${id} with data:`, data);
      const response = await apiClient.put<T>(`${this.config.basePath}${id}/`, data);
      console.log(`Updated ${this.config.entityName}:`, response.data);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Partially update an existing entity
   */
  async patch(id: number, data: Partial<TUpdate>): Promise<T> {
    try {
      console.log(`Patching ${this.config.entityName} ${id} with data:`, data);
      const response = await apiClient.patch<T>(`${this.config.basePath}${id}/`, data);
      console.log(`Patched ${this.config.entityName}:`, response.data);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Delete an entity
   */
  async delete(id: number): Promise<void> {
    try {
      console.log(`Deleting ${this.config.entityName} ${id}`);
      await apiClient.delete(`${this.config.basePath}${id}/`);
      console.log(`Deleted ${this.config.entityName} ${id}`);
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Check if an entity exists
   */
  async exists(id: number): Promise<boolean> {
    try {
      await this.getById(id);
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Count total entities with optional filtering
   */
  async count(params?: TListParams): Promise<number> {
    try {
      const response = await apiClient.get<any>(this.config.basePath, { params });
      const parsed = parseApiResponse<T>(response.data, this.config.arrayKeys);
      return parsed.totalCount;
    } catch (error) {
      console.error(`Failed to count ${this.config.entityName}:`, error);
      return 0;
    }
  }

  /**
   * Get entities with pagination info
   */
  async getPaginated(params?: TListParams & { page?: number; page_size?: number }) {
    try {
      const response = await apiClient.get<any>(this.config.basePath, { params });
      const parsed = parseApiResponse<T>(response.data, this.config.arrayKeys);
      
      return {
        data: parsed.data,
        totalCount: parsed.totalCount,
        hasNext: parsed.hasNext,
        hasPrevious: parsed.hasPrevious,
      };
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Bulk create entities
   */
  async bulkCreate(data: TCreate[]): Promise<T[]> {
    try {
      console.log(`Bulk creating ${this.config.entityName}:`, data);
      const response = await apiClient.post<T[]>(`${this.config.basePath}bulk/`, data);
      console.log(`Bulk created ${this.config.entityName}:`, response.data);
      return Array.isArray(response.data) ? response.data : [response.data];
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Bulk update entities
   */
  async bulkUpdate(updates: Array<{ id: number; data: TUpdate }>): Promise<T[]> {
    try {
      console.log(`Bulk updating ${this.config.entityName}:`, updates);
      const response = await apiClient.patch<T[]>(`${this.config.basePath}bulk/`, updates);
      console.log(`Bulk updated ${this.config.entityName}:`, response.data);
      return Array.isArray(response.data) ? response.data : [response.data];
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Bulk delete entities
   */
  async bulkDelete(ids: number[]): Promise<void> {
    try {
      console.log(`Bulk deleting ${this.config.entityName}:`, ids);
      await apiClient.delete(`${this.config.basePath}bulk/`, { data: { ids } });
      console.log(`Bulk deleted ${this.config.entityName}`);
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Search entities
   */
  async search(query: string, params?: TListParams): Promise<T[]> {
    try {
      const searchParams = { ...params, search: query };
      const response = await apiClient.get<any>(this.config.basePath, { params: searchParams });
      
      return extractDataArray<T>(response.data, this.config.arrayKeys);
    } catch (error) {
      console.error(`Failed to search ${this.config.entityName}:`, error);
      return [];
    }
  }

  /**
   * Get entities filtered by a specific field value
   */
  async getByField(field: keyof T, value: any): Promise<T[]> {
    try {
      const params = { [field as string]: value };
      return await this.getAll(params as TListParams);
    } catch (error) {
      console.error(`Failed to get ${this.config.entityName} by ${String(field)}:`, error);
      return [];
    }
  }

  /**
   * Get the first entity that matches the criteria
   */
  async findOne(params?: TListParams): Promise<T | null> {
    try {
      const entities = await this.getAll(params);
      return entities.length > 0 ? entities[0] : null;
    } catch (error) {
      console.error(`Failed to find one ${this.config.entityName}:`, error);
      return null;
    }
  }

  /**
   * Check if any entities match the criteria
   */
  async any(params?: TListParams): Promise<boolean> {
    try {
      const entity = await this.findOne(params);
      return entity !== null;
    } catch (error) {
      console.error(`Failed to check if any ${this.config.entityName} exist:`, error);
      return false;
    }
  }

  /**
   * Hook methods that can be overridden by subclasses
   */
  protected beforeCreate?(data: TCreate): TCreate | Promise<TCreate>;
  protected afterCreate?(entity: T): T | Promise<T>;
  protected beforeUpdate?(id: number, data: TUpdate): TUpdate | Promise<TUpdate>;
  protected afterUpdate?(entity: T): T | Promise<T>;
  protected beforeDelete?(id: number): void | Promise<void>;
  protected afterDelete?(id: number): void | Promise<void>;
}